// src/pages/dashboard/Dashboard.js
import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const fmt = (n) => parseFloat(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 });

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [revenue, setRevenue] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    Promise.all([
      api.get('/dashboard'),
      api.get('/reports/revenue')
    ]).then(([dash, rev]) => {
      setData(dash.data);
      setRevenue(rev.data.data.slice(0, 6).reverse());
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="loading-page">
      <div className="spinner" style={{ width: 32, height: 32 }}></div>
      <span>Loading dashboard...</span>
    </div>
  );

  const stats = [
    {
      label: 'Active Customers',
      value: data?.customers?.active || 0,
      icon: '👥',
      color: '#e8f0f9', iconColor: '#0f4c81',
      sub: `${data?.customers?.total || 0} total`
    },
    {
      label: 'Total Outstanding',
      value: `KES ${fmt(data?.invoices?.outstanding)}`,
      icon: '📋',
      color: '#fdecea', iconColor: '#e63946',
      sub: `${data?.overdue?.count || 0} overdue accounts`
    },
    {
      label: "Today's Collections",
      value: `KES ${fmt(data?.payments_today?.today)}`,
      icon: '💳',
      color: '#e0f5f2', iconColor: '#2a9d8f',
      sub: 'Cash received today'
    },
    {
      label: 'Total Billed',
      value: `KES ${fmt(data?.invoices?.billed)}`,
      icon: '🧾',
      color: '#fef3e8', iconColor: '#f4a261',
      sub: `KES ${fmt(data?.invoices?.collected)} collected`
    },
    {
      label: 'Disconnected',
      value: data?.disconnections?.count || 0,
      icon: '🚫',
      color: '#fdecea', iconColor: '#e63946',
      sub: 'Pending reconnection'
    },
    {
      label: 'Low Stock Items',
      value: data?.low_stock?.count || 0,
      icon: '📦',
      color: '#fef3e8', iconColor: '#f4a261',
      sub: 'Below reorder level'
    },
  ];

  return (
    <div className="page-body">
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p>Welcome back, {user?.full_name}. Here's what's happening today.</p>
        </div>
        <div style={{ fontSize: 13, color: '#64748b' }}>
          {new Date().toLocaleDateString('en-KE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stat-grid">
        {stats.map((s, i) => (
          <div key={i} className="stat-card">
            <div>
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ fontSize: s.value?.toString().length > 10 ? 15 : 22 }}>
                {s.value}
              </div>
              <div className="stat-sub">{s.sub}</div>
            </div>
            <div className="stat-icon" style={{ background: s.color, color: s.iconColor, fontSize: 22 }}>
              {s.icon}
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>

        {/* Revenue Chart */}
        <div className="card">
          <div className="card-header">
            <h3>Revenue by Period</h3>
          </div>
          {revenue.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={revenue}>
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
                <Tooltip formatter={v => `KES ${parseFloat(v).toLocaleString()}`} />
                <Bar dataKey="total_billed" fill="#0f4c81" radius={[4,4,0,0]} name="Billed" />
                <Bar dataKey="total_collected" fill="#00a896" radius={[4,4,0,0]} name="Collected" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state" style={{ padding: 40 }}>
              <p>No billing data yet</p>
            </div>
          )}
        </div>

        {/* Overdue Summary */}
        <div className="card">
          <div className="card-header">
            <h3>Arrears Summary</h3>
          </div>
          <div style={{ padding: '8px 0' }}>
            {[
              { label: 'Current (0-30 days)', color: '#2a9d8f', pct: 30 },
              { label: '31-60 days', color: '#f4a261', pct: 25 },
              { label: '61-90 days', color: '#e76f51', pct: 20 },
              { label: 'Over 90 days', color: '#e63946', pct: 25 },
            ].map((item, i) => (
              <div key={i} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>{item.label}</span>
                </div>
                <div style={{ height: 8, background: '#f1f5f9', borderRadius: 99 }}>
                  <div style={{ height: '100%', width: `${item.pct}%`, background: item.color, borderRadius: 99 }} />
                </div>
              </div>
            ))}
            <div style={{ marginTop: 16 }}>
              <a href="/reports/aged-analysis" className="btn btn-outline btn-sm">
                View Full Aged Analysis →
              </a>
            </div>
          </div>
        </div>

      </div>

      {/* Quick Actions */}
      <div className="card">
        <div className="card-header"><h3>Quick Actions</h3></div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {[
            { label: '+ New Customer',    to: '/customers/new',  style: 'btn-primary' },
            { label: '+ Record Payment',  to: '/payments/new',   style: 'btn-accent' },
            { label: '+ Meter Reading',   to: '/billing/readings', style: 'btn-outline' },
            { label: '+ Maintenance Job', to: '/maintenance/new', style: 'btn-outline' },
            { label: 'Generate Invoices', to: '/billing/generate', style: 'btn-outline' },
            { label: 'Aged Analysis',     to: '/reports/aged-analysis', style: 'btn-outline' },
          ].map((a, i) => (
            <a key={i} href={a.to} className={`btn ${a.style}`}>{a.label}</a>
          ))}
        </div>
      </div>
    </div>
  );
}
